import React from 'react';
import { BEM } from '@feedfm/feed-util';

import './PageError.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { ErrorDisplay } from '~/components/ErrorDisplay';
import { SidebarPageTemplate } from '~/components/SidebarPageTemplate/SidebarPageTemplate';
import { Navigation } from '~/components/Navigation/Navigation';

const bem = new BEM( 'ops-page-error', [] );

export interface PageErrorProps {
  summary?: string;
  error: any;

  /**
   * This is niche and for rendering a page-level error outside of the normal stack of contexts and
   * wrappers defined in App.tsx.
   *
   * With standalone off (default) PageError it assumes it is rendering under the normal <App />
   * stack, which includes branding, navigation and some context providers.
   *
   * With standlone on, PageError will render the navigation and container. It will further render
   * the navigation in safe mode. These things allow it to be rendered outside of the normal <App />
   * stack, which is for last-ditch error handling scenarios.
   */
  standalone?: boolean;
}

const PageErrorEmbedded = ( { summary, error }: PageErrorProps ) => (
  <SidebarPageTemplate
    data-testid="ops-page-error"
    className={ BEM.$( bem ) }
    hideSidebar
    title={
      <>
        <div className={ BEM.$( bem.el( 'heading' ) ) }>
          <div className={ BEM.$( bem.el( 'icon' ) ) }>
            <FontAwesomeIcon icon={ faTimesCircle } />
          </div>
          { summary || 'An error has occurred!' }
        </div>
      </>
    }
    main={
      <>
        <div className={ BEM.$( bem.el( 'details' ) ) }>
          <ErrorDisplay error={ error } />
        </div>
      </>
    }
  />
);

/**
 * This component is for rendering a full-page error message, typically in a main content area in
 * response to something catastrophic.
 */
export const PageError = ( props: PageErrorProps ) =>
  props.standalone === false ? (
    <PageErrorEmbedded { ...props } />
  ) : (
    <>
      <Navigation safeMode />
      <div id="main" className="h-100 container-fluid">
        <PageErrorEmbedded { ...props } />
      </div>
    </>
  );

PageError.displayName = 'PageError';
